import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';
import { IOrderObject, IRPCData } from '~api/CartAPI/ICartApiSdk';
import { defaultValue } from '~api/CartAPI/utils/utils';
import { ICartOrderData } from '~domain/entities/Cart/ICart';
import { captureMessage } from '@sentry/react';
import { IResponse } from '@estee/elc-http-transport';
import { toBoolean } from '~utils/ToBoolean';

let setTransId: number;

export enum HttpMethod {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    PATCH = 'patch',
    DELETE = 'delete'
}
export interface IHeaders {
    headers?: {
        [header: string]: string;
    };
}

export interface IRequestParams {
    method: HttpMethod;
    headers?: { [key: string]: string };
    body?: string | Record<string, unknown>;
    skuId?: number;
    transId?: number;
    deleteMessages?: boolean;
    signal?: AbortSignal;
}

export const reduceToCartOrderData = ({ response }: IRPCData<IOrderObject>): ICartOrderData => {
    const {
        messages,
        offers,
        user,
        wp_samples,
        pwp,
        order: { giftWrap, SHIP_METHOD, address }
    }: IOrderObject =
        response.data?.[0].result?.value || ((defaultValue as unknown) as IOrderObject);
    const is_giftWrap = toBoolean(giftWrap);
    const ADDRESS_ID = address?.ADDRESS_ID ?? 0;

    return { messages, offers, user, is_giftWrap, SHIP_METHOD, wp_samples, pwp, ADDRESS_ID };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAuth = (config: any) => {
    if ('restApiCredentials' in config && config.restApiCredentials !== '') {
        return config.restApiCredentials;
    }

    return btoa(process.env.CART_REST_API_TOKEN || '');
};

export const makeRestAPIRequest = async <R>(params: IRequestParams) => {
    try {
        const { method, headers, body, skuId, transId, deleteMessages } = params;
        const { config } = diContainer.get(serviceNames.config);
        const { perlgem } = config;
        const auth = getAuth(config);
        const { brand_id, region_id } = perlgem;
        const brandRegionQuery = `?brand=${brand_id}&region=${region_id}`;
        const deleteMessagesQuery = deleteMessages ? '&delete_messages=1' : '';
        let url = '/rest/api/v1/cart';

        if (!brand_id && !region_id) {
            return {} as IResponse<R[]>;
        }

        switch (method) {
            case 'post':
                url += `/${transId}/${brandRegionQuery}`;
                break;

            case 'delete':
                url += `/${transId}/sku_base_id/${skuId}${brandRegionQuery}`;
                break;

            case 'get':
                url += `${brandRegionQuery}${deleteMessagesQuery}`;
                break;

            default:
                url += `${brandRegionQuery}`;
        }

        const data = await fetch(url, {
            method,
            headers: {
                Authorization: `Basic ${auth}`,
                'Content-Type': 'application/json',
                ...headers
            },
            signal: params.signal,
            body: JSON.stringify(body)
        });

        const response = await data.json();

        // Match existing RPC data structure.
        response.data = [response];

        const value = response?.data?.[0].result.value;
        setTransId = value?.trans?.TRANS_ID;

        return response as IResponse<R[]>;
    } catch (error) {
        if (error.name !== 'AbortError') {
            captureMessage('Rest API [makeRestAPIRequest] - call failed', {
                level: 'warning',
                tags: { api: true, custom: true },
                extra: { error }
            });
        }

        return {} as IResponse<R[]>;
    }
};

export const getTransId = () => {
    return setTransId;
};

export const isCartRestAPI = () => {
    const { config } = diContainer.get(serviceNames.config) || {};

    return !!config?.restApiEnabled;
};
